import React, { useMemo } from 'react';
import {
  SessionReportRecord,
  buildCaseCounselingTrail,
  formatPassageTrailLine,
  formatThemeTrailLine,
} from './sessionReportsUtils';
import { t } from '../../utils/translations';
import ChipCarousel from '../common/ChipCarousel';

interface CaseCounselingTrailProps {
  reports: SessionReportRecord[];
  /** compact = one/two lines for reports header; full = labeled section rows */
  variant?: 'full' | 'compact';
  className?: string;
  /**
   * Highlights the theme/passage segment for this session — full variant only. The trail stays
   * the same read-only overview it always was; this just points at whichever session the
   * separate session picker has selected, so the two sections read as connected.
   */
  selectedSessionNumber?: number | null;
  /**
   * Only wired once there are enough entries to carousel: a segment becomes a chip at that point,
   * and a chip that cannot be picked reads as broken, not as a passive summary anymore.
   */
  onSelectSession?: (sessionNumber: number) => void;
}

const CaseCounselingTrailView: React.FC<CaseCounselingTrailProps> = ({
  reports,
  variant = 'full',
  className = '',
  selectedSessionNumber = null,
  onSelectSession,
}) => {
  const trail = useMemo(() => buildCaseCounselingTrail(reports), [reports]);

  if (variant === 'compact') {
    const themeLine = formatThemeTrailLine(trail);
    const passageLine = formatPassageTrailLine(trail);
    if (!themeLine && !passageLine) return null;
    return (
      <div className={`space-y-1 ${className}`}>
        {themeLine && (
          <p className="text-xs leading-relaxed text-slate-600">
            <span className="font-semibold text-slate-700">
              {t.sessionReports.counselingTrail.theme}:{' '}
            </span>
            {themeLine}
          </p>
        )}
        {passageLine && (
          <p className="text-xs leading-relaxed text-slate-600">
            <span className="font-semibold text-slate-700">
              {t.sessionReports.counselingTrail.passages}:{' '}
            </span>
            {passageLine}
          </p>
        )}
      </div>
    );
  }

  if (trail.themes.length === 0 && trail.passages.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      {trail.themes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500">
            {t.sessionReports.counselingTrail.theme}
          </p>
          <ChipCarousel
            className="mt-1.5"
            items={trail.themes.map((item) => item.sessionNumber)}
            selected={selectedSessionNumber}
            onSelect={onSelectSession ?? (() => {})}
            getLabel={(sessionNumber) => {
              const item = trail.themes.find((i) => i.sessionNumber === sessionNumber);
              return item ? `#${item.roadSession} ${item.theme}` : '';
            }}
            previousLabel={t.meetingNotes.previousSessions}
            nextLabel={t.meetingNotes.nextSessions}
          />
        </div>
      )}
      {trail.passages.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500">
            {t.sessionReports.counselingTrail.passages}
          </p>
          <ChipCarousel
            className="mt-1.5"
            items={trail.passages.map((item) => item.sessionNumber)}
            selected={selectedSessionNumber}
            onSelect={onSelectSession ?? (() => {})}
            getLabel={(sessionNumber) => {
              const item = trail.passages.find((i) => i.sessionNumber === sessionNumber);
              return item ? `#${item.roadSession} ${item.passage}` : '';
            }}
            previousLabel={t.meetingNotes.previousSessions}
            nextLabel={t.meetingNotes.nextSessions}
          />
        </div>
      )}
    </div>
  );
};

export default CaseCounselingTrailView;
